import { resumoDasBuscas } from "../dashboard/components/search-terms-growth/search-terms-summary";
import type { IDashboardData, ISearchTermsGrowth } from "../dashboard/types";
import { resumoDasPosicoes } from "../relatorio/dados/buscas";
import { numero, paraData } from "../relatorio/dados/formatar";
import { variacaoDoPeriodo } from "../relatorio/dados/periodo";
import { estrelas, impulsionamentosDoPeriodo, respostasDoPeriodo } from "../relatorio/dados/trabalho";
import { maiorAvanco } from "../relatorio/dados/winback";
import type { IConcorrentes, IHistoricoDePosicoes } from "../relatorio/types";

/**
 * Modelos de notificação da tela "Enviar push". Cada modelo só aparece
 * disponível quando o dado do cliente sustenta a mensagem, e o texto já sai
 * preenchido com os mesmos números do relatório que o cliente vê ao tocar.
 * Base: pesquisa de push de 04/10/2026 (recibo de valor, nada inventado).
 */

export const LIMITE_TITULO = { ideal: 40, maximo: 60 };
export const LIMITE_TEXTO = { ideal: 120, maximo: 240 };

export type Categoria = "CONQUISTA" | "RESULTADO" | "TRABALHO" | "AVALIACOES" | "DESCOBERTA" | "CONCORRENCIA" | "RESUMO";

export const CATEGORIAS: { id: Categoria; rotulo: string; icone: string }[] = [
  { id: "CONQUISTA", rotulo: "Conquistas", icone: "emoji_events" },
  { id: "RESULTADO", rotulo: "Resultados", icone: "call" },
  { id: "TRABALHO", rotulo: "Trabalho feito", icone: "task_alt" },
  { id: "AVALIACOES", rotulo: "Avaliações", icone: "star" },
  { id: "DESCOBERTA", rotulo: "Descoberta", icone: "travel_explore" },
  { id: "CONCORRENCIA", rotulo: "Concorrência", icone: "leaderboard" },
  { id: "RESUMO", rotulo: "Resumo", icone: "summarize" },
];

/** Seções do relatório que o toque pode abrir (ids em relatorio-completo.tsx). */
export const SECOES: { id: string; rotulo: string }[] = [
  { id: "resultados", rotulo: "Resultado do período" },
  { id: "rankings", rotulo: "Buscas que trabalhamos" },
  { id: "buscas", rotulo: "Como encontraram a empresa" },
  { id: "concorrentes", rotulo: "Mercado e concorrentes" },
  { id: "trabalho", rotulo: "Trabalho por trás do resultado" },
  { id: "avaliacoes", rotulo: "Avaliações" },
  { id: "impulsionamentos", rotulo: "Impulsionamentos" },
];

export const linkDaSecao = (publicId: string, secao: string) =>
  `/home?perfil=${publicId}${secao ? `&secao=${secao}` : ""}`;

export type DadosDoCliente = {
  nome: string;
  ligacoes: number | null;
  rotas: number | null;
  acessosSite: number | null;
  aparicoesMaps: number | null;
  variacaoLigacoes: number | null;
  variacaoRotas: number | null;
  buscasMedidas: number;
  em1: number;
  top3: number;
  top10: number;
  avanco: { busca: string; antes: number; agora: number } | null;
  respostasSemana: number;
  postsSemana: number;
  respostas30: number;
  posts30: number;
  cincoEstrelasSemana: { respondida: boolean } | null;
  percentualSemNome: number | null;
  concorrenteAtras: { nome: string; aparece: number; voce: number; medidas: number } | null;
};

const DIA = 86_400_000;

/** Nome curto para caber no título/corpo (corta em palavra). */
export const nomeCurto = (nome: string, limite = 26): string => {
  const limpo = (nome || "").trim();
  if (limpo.length <= limite) return limpo;
  const corte = limpo.slice(0, limite + 1);
  const espaco = corte.lastIndexOf(" ");
  return (espaco > 10 ? corte.slice(0, espaco) : limpo.slice(0, limite)).replace(/[\s,.;:-]+$/, "");
};

export const plural = (n: number, um: string, varios: string) => `${numero(n)} ${n === 1 ? um : varios}`;

const positivo = (valor: number | null | undefined): number | null =>
  typeof valor === "number" && valor > 0 ? valor : null;

export const dadosDoCliente = (
  perfil: IDashboardData,
  extras: { historico?: IHistoricoDePosicoes | null; concorrentes?: IConcorrentes | null; pesquisas?: ISearchTermsGrowth | null },
  hoje: Date = new Date(),
): DadosDoCliente => {
  const analytics = perfil.analytics;
  const temAnalytics = !!analytics && [analytics.callClicks, analytics.requestedRoutes, analytics.websiteClicks, analytics.viewsInMap].some((v) => (v ?? 0) > 0);
  const posicoes = resumoDasPosicoes(perfil.keywords, hoje);
  const avanco = maiorAvanco(extras.historico);
  const reviews = perfil.reviews ?? [];
  const cincoEstrelas = reviews.find((review) => {
    const quando = paraData(review.createTime);
    return estrelas(review.starRating) === 5 && quando !== null && hoje.getTime() - quando.getTime() <= 7 * DIA;
  });
  const buscas = extras.pesquisas && extras.pesquisas.totalByMonth.length ? resumoDasBuscas(extras.pesquisas) : null;
  const concorrentes = extras.concorrentes;
  const atras =
    concorrentes && concorrentes.keywordsMeasured > 0 && concorrentes.competitors.length > 0 &&
    concorrentes.competitors.every((empresa) => empresa.appearsIn < concorrentes.profile.appearsIn)
      ? [...concorrentes.competitors].sort((a, b) => b.appearsIn - a.appearsIn)[0]
      : null;

  return {
    nome: perfil.profileName || "",
    ligacoes: temAnalytics ? analytics.callClicks ?? 0 : null,
    rotas: temAnalytics ? analytics.requestedRoutes ?? 0 : null,
    acessosSite: temAnalytics ? analytics.websiteClicks ?? 0 : null,
    aparicoesMaps: temAnalytics ? analytics.viewsInMap ?? 0 : null,
    variacaoLigacoes: variacaoDoPeriodo(perfil.chartData, "CALLS", 30, hoje),
    variacaoRotas: variacaoDoPeriodo(perfil.chartData, "DIRECTIONS", 30, hoje),
    buscasMedidas: posicoes.medidas,
    em1: posicoes.em1,
    top3: posicoes.top3,
    top10: posicoes.top10,
    avanco:
      avanco && avanco.situacao === "subiu" && avanco.antes.posicao > 0 && avanco.depois.posicao > 0
        ? { busca: avanco.titulo, antes: avanco.antes.posicao, agora: avanco.depois.posicao }
        : null,
    respostasSemana: respostasDoPeriodo(reviews, 7, hoje).length,
    postsSemana: impulsionamentosDoPeriodo(perfil.posts, 7, hoje).length,
    respostas30: respostasDoPeriodo(reviews, 30, hoje).length,
    posts30: impulsionamentosDoPeriodo(perfil.posts, 30, hoje).length,
    cincoEstrelasSemana: cincoEstrelas ? { respondida: !!cincoEstrelas.replied } : null,
    percentualSemNome: positivo(buscas?.percentualPorServico ?? null),
    concorrenteAtras:
      atras && concorrentes
        ? { nome: atras.name, aparece: atras.appearsIn, voce: concorrentes.profile.appearsIn, medidas: concorrentes.keywordsMeasured }
        : null,
  };
};

export type Modelo = {
  id: string;
  categoria: Categoria;
  secao: string;
  titulo: (d: DadosDoCliente) => string;
  texto: (d: DadosDoCliente) => string;
  /** Motivo de não estar disponível para este cliente, ou null quando o dado sustenta. */
  falta: (d: DadosDoCliente) => string | null;
};

const lista = (partes: string[]) =>
  partes.length <= 1 ? partes[0] ?? "" : `${partes.slice(0, -1).join(", ")} e ${partes[partes.length - 1]}`;

const trabalhoDe = (respostas: number, posts: number) =>
  lista([
    respostas > 0 ? `respondemos ${plural(respostas, "avaliação", "avaliações")}` : "",
    posts > 0 ? `publicamos ${plural(posts, "postagem", "postagens")}` : "",
  ].filter(Boolean));

export const MODELOS: Modelo[] = [
  {
    id: "rank-1-lugar",
    categoria: "CONQUISTA",
    secao: "rankings",
    titulo: () => "Chegou ao 1º lugar no Google",
    texto: (d) => `${nomeCurto(d.nome)}: "${d.avanco?.busca}" saiu de ${d.avanco?.antes}º para 1º. Veja a evolução.`,
    falta: (d) => (d.avanco && d.avanco.agora === 1 ? null : "Nenhuma busca chegou ao 1º lugar na última medição"),
  },
  {
    id: "rank-maior-avanco",
    categoria: "CONQUISTA",
    secao: "rankings",
    titulo: () => "Seu maior avanço no Google",
    texto: (d) => `"${d.avanco?.busca}" foi de ${d.avanco?.antes}º para ${d.avanco?.agora}º. Confira a evolução completa.`,
    falta: (d) => (d.avanco ? null : "Sem avanço medido entre as duas últimas medições"),
  },
  {
    id: "rank-em-1",
    categoria: "CONQUISTA",
    secao: "rankings",
    titulo: (d) => (d.em1 === 1 ? "Sua empresa em 1º no Google" : `${d.em1} buscas em 1º lugar`),
    texto: (d) => `${nomeCurto(d.nome)} está em 1º lugar em ${plural(d.em1, "busca acompanhada", "buscas acompanhadas")} no Google. Veja quais.`,
    falta: (d) => (d.em1 > 0 ? null : "Nenhuma busca em 1º lugar"),
  },
  {
    id: "rank-top3",
    categoria: "CONQUISTA",
    secao: "rankings",
    titulo: () => "Mais presença nas buscas locais",
    texto: (d) => `${plural(d.top3, "busca está", "buscas estão")} entre as 3 primeiras e ${numero(d.top10)} no Top 10. Confira a evolução.`,
    falta: (d) => (d.top3 > 0 ? null : "Nenhuma busca no Top 3"),
  },
  {
    id: "resultado-acoes",
    categoria: "RESULTADO",
    secao: "resultados",
    titulo: () => "Seu Google trouxe clientes",
    texto: (d) =>
      `Nos últimos 30 dias: ${plural(d.ligacoes ?? 0, "ligação", "ligações")}, ${plural(d.rotas ?? 0, "pedido", "pedidos")} de rota e ${plural(d.acessosSite ?? 0, "acesso", "acessos")} ao site.`,
    falta: (d) => ((d.ligacoes ?? 0) + (d.rotas ?? 0) + (d.acessosSite ?? 0) > 0 ? null : "Sem dados do Google nos últimos 30 dias"),
  },
  {
    id: "resultado-ligacoes",
    categoria: "RESULTADO",
    secao: "resultados",
    titulo: () => "Mais ligações pelo Google",
    texto: (d) =>
      `${nomeCurto(d.nome)} recebeu ${plural(d.ligacoes ?? 0, "ligação", "ligações")} pelo Google em 30 dias, +${d.variacaoLigacoes}% sobre o mês anterior.`,
    falta: (d) => ((d.variacaoLigacoes ?? 0) > 0 && (d.ligacoes ?? 0) > 0 ? null : "Ligações não cresceram sobre o mês anterior"),
  },
  {
    id: "resultado-rotas",
    categoria: "RESULTADO",
    secao: "resultados",
    titulo: () => "Mais gente indo até você",
    texto: (d) =>
      `Foram ${plural(d.rotas ?? 0, "pedido", "pedidos")} de rota até ${nomeCurto(d.nome)} em 30 dias${(d.variacaoRotas ?? 0) > 0 ? `, +${d.variacaoRotas}% sobre o mês anterior` : ""}.`,
    falta: (d) => ((d.rotas ?? 0) > 0 ? null : "Sem pedidos de rota nos últimos 30 dias"),
  },
  {
    id: "trabalho-semana",
    categoria: "TRABALHO",
    secao: "trabalho",
    titulo: () => "Trabalho da semana concluído",
    texto: (d) => `Nesta semana ${trabalhoDe(d.respostasSemana, d.postsSemana)} no seu perfil do Google. Veja cada entrega.`,
    falta: (d) => (d.respostasSemana + d.postsSemana > 0 ? null : "Nenhuma resposta ou postagem nos últimos 7 dias"),
  },
  {
    id: "trabalho-mes",
    categoria: "TRABALHO",
    secao: "trabalho",
    titulo: () => "O que fizemos neste mês",
    texto: (d) =>
      `Em 30 dias ${trabalhoDe(d.respostas30, d.posts30)}${d.top3 > 0 ? `, e ${plural(d.top3, "busca segue", "buscas seguem")} no Top 3` : ""}.`,
    falta: (d) => (d.respostas30 + d.posts30 > 0 ? null : "Nenhuma resposta ou postagem nos últimos 30 dias"),
  },
  {
    id: "avaliacao-5-estrelas",
    categoria: "AVALIACOES",
    secao: "avaliacoes",
    titulo: () => "Nova avaliação 5 estrelas",
    texto: (d) =>
      `${nomeCurto(d.nome)} recebeu uma avaliação 5 estrelas no Google.${d.cincoEstrelasSemana?.respondida ? " Já respondemos por você." : " Veja a avaliação."}`,
    falta: (d) => (d.cincoEstrelasSemana ? null : "Nenhuma avaliação 5 estrelas nos últimos 7 dias"),
  },
  {
    id: "avaliacoes-respondidas",
    categoria: "AVALIACOES",
    secao: "avaliacoes",
    titulo: () => "Avaliações respondidas",
    texto: (d) => `Respondemos ${plural(d.respostas30, "avaliação", "avaliações")} em 30 dias. Quem pesquisa vê que sua empresa responde.`,
    falta: (d) => (d.respostas30 > 0 ? null : "Nenhuma avaliação respondida nos últimos 30 dias"),
  },
  {
    id: "descoberta-sem-nome",
    categoria: "DESCOBERTA",
    secao: "buscas",
    titulo: () => "Clientes acharam você sem o nome",
    texto: (d) => `${d.percentualSemNome}% das pesquisas que mostraram sua empresa não citavam o nome dela. É cliente novo te achando.`,
    falta: (d) => (d.percentualSemNome ? null : "Sem dados de pesquisas dos últimos meses"),
  },
  {
    id: "concorrencia-a-frente",
    categoria: "CONCORRENCIA",
    secao: "concorrentes",
    titulo: () => "À frente da concorrência",
    texto: (d) =>
      `${nomeCurto(d.nome)} aparece em ${d.concorrenteAtras?.voce} de ${d.concorrenteAtras?.medidas} buscas medidas, mais que ${nomeCurto(d.concorrenteAtras?.nome ?? "", 22)}.`,
    falta: (d) => (d.concorrenteAtras ? null : "A empresa não está à frente de todos os concorrentes medidos"),
  },
  {
    id: "resumo-mes",
    categoria: "RESUMO",
    secao: "resultados",
    titulo: () => "Seu mês em 1 minuto",
    texto: (d) => `Resultados, posições e o trabalho feito em ${nomeCurto(d.nome)} nos últimos 30 dias, num resumo rápido.`,
    falta: () => null,
  },
];

export const variaveisParaInserir = (d: DadosDoCliente): { rotulo: string; valor: string }[] =>
  [
    { rotulo: "Empresa", valor: nomeCurto(d.nome) },
    { rotulo: "Ligações (30 dias)", valor: d.ligacoes === null ? "" : numero(d.ligacoes) },
    { rotulo: "Pedidos de rota (30 dias)", valor: d.rotas === null ? "" : numero(d.rotas) },
    { rotulo: "Acessos ao site (30 dias)", valor: d.acessosSite === null ? "" : numero(d.acessosSite) },
    { rotulo: "Buscas no Top 3", valor: d.buscasMedidas ? numero(d.top3) : "" },
    { rotulo: "Buscas em 1º", valor: d.buscasMedidas ? numero(d.em1) : "" },
    { rotulo: "Maior avanço", valor: d.avanco ? `"${d.avanco.busca}" de ${d.avanco.antes}º para ${d.avanco.agora}º` : "" },
    { rotulo: "Respostas (7 dias)", valor: numero(d.respostasSemana) },
    { rotulo: "Postagens (7 dias)", valor: numero(d.postsSemana) },
  ].filter((item) => item.valor !== "");

export type SituacaoDoTexto = "ok" | "longo" | "excede";

export const situacaoDoTamanho = (texto: string, limite: { ideal: number; maximo: number }): SituacaoDoTexto =>
  texto.length > limite.maximo ? "excede" : texto.length > limite.ideal ? "longo" : "ok";
